import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { formatISTDateTime } from "@/lib/time";
import EmptyHero from "@/components/hub/EmptyHero";
import MediaBubble from "@/components/hub/MediaBubble";
import CallDriverDialog from "@/components/hub/CallDriverDialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Phone,
  PaperPlaneTilt,
  Check,
  Checks,
  X,
  BookmarkSimple,
  Plus,
  Trash,
  MagnifyingGlass,
  DownloadSimple,
  UserCircle,
  CircleNotch,
  Paperclip,
  Smiley,
  CaretLeft,
  CheckCircle,
  ArrowCounterClockwise,
  PhoneCall,
  Robot,
} from "@phosphor-icons/react";

function initials(name, phone) {
  const src = (name || phone || "?").trim();
  return src.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

function EmptyState() {
  return <EmptyHero />;
}

function Tick({ status }) {
  if (status === "read") return <Checks size={14} weight="bold" className="text-[#60A5FA]" />;
  if (status === "delivered") return <Checks size={14} weight="bold" className="text-white/60" />;
  if (status === "failed") return <X size={14} weight="bold" className="text-[#FCA5A5]" />;
  return <Check size={14} weight="bold" className="text-white/60" />;
}

export default function ChatWindow({ contact, messages, onBack, mobileVisible = false }) {
  const endRef = useRef(null);
  const typingFiredRef = useRef({});
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [inChatQuery, setInChatQuery] = useState("");
  const [showCallDialog, setShowCallDialog] = useState(false);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, contact?.phone]);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  const filteredMessages = useMemo(() => {
    const q = inChatQuery.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => (m.text || "").toLowerCase().includes(q));
  }, [messages, inChatQuery]);

  const send = async () => {
    const body = text.trim();
    if (!body || !contact || sending) return;
    setSending(true);
    try {
      await api.post("/reply", { to: contact.phone, text: body });
      setText("");
      toast.success("Reply sent");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && (d.hint || d.message)) || (typeof d === "string" ? d : null) || e.message || "Failed to send";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSending(false);
    }
  };

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadAndSend = async (file) => {
    if (!file || !contact) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("wh_token");
      const up = await fetch(`${API_BASE}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!up.ok) throw new Error("Upload failed");
      const { media_id } = await up.json();
      const kind = file.type.startsWith("image/") ? "image"
        : file.type.startsWith("video/") ? "video"
        : file.type.startsWith("audio/") ? "audio"
        : "document";
      await api.post("/reply/media", {
        to: contact.phone,
        media_id,
        kind,
        caption: text.trim() || undefined,
        filename: kind === "document" ? file.name : undefined,
      });
      setText("");
      toast.success(`${kind} sent`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e.message || "Send failed");
    } finally {
      setUploading(false);
    }
  };

  const react = async (message_id, emoji) => {
    try {
      await api.post("/reactions", { to: contact.phone, message_id, emoji });
    } catch (_) {
      toast.error("Reaction failed");
    }
  };

  // Typing indicator
  useEffect(() => {
    if (!contact || !text) return;
    const lastIn = [...messages].reverse().find((m) => m.direction === "inbound");
    if (!lastIn || !lastIn.id || !lastIn.id.startsWith("wamid")) return;
    const firedKey = `typed:${lastIn.id}`;
    const lastFired = typingFiredRef.current[firedKey] || 0;
    if (Date.now() - lastFired < 25_000) return;
    const t = setTimeout(() => {
      typingFiredRef.current[firedKey] = Date.now();
      api.post("/typing", { message_id: lastIn.id }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [text, contact?.phone]);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/conversations/${contact.phone}/status`, { status });
      if (status === "resolved") toast.success("Ticket closed");
      else if (status === "in_progress") toast.success("Ticket re-opened");
      else toast.success(`Marked as ${status.replace("_", " ")}`);
    } catch (_) {
      toast.error("Failed to update status");
    }
  };

  const closeTicket = () => updateStatus("resolved");
  const reopenTicket = () => updateStatus("in_progress");

  const assign = async (user_id) => {
    try {
      await api.patch(`/conversations/${contact.phone}/assign`, { user_id: user_id || null });
      toast.success(user_id ? "Assigned" : "Unassigned");
    } catch (_) {
      toast.error("Failed to assign");
    }
  };

  const toggleBot = async () => {
    try {
      await api.patch(`/conversations/${contact.phone}/bot`, { paused: !contact.bot_paused });
      toast.success(contact.bot_paused ? "Bot resumed for this chat" : "Bot paused for this chat");
    } catch (_) {
      toast.error("Failed to update bot");
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("wh_token");
      const res = await fetch(`${API_BASE}/conversations/${contact.phone}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `conversation_${contact.phone}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Export failed");
    }
  };

  const addTemplate = async () => {
    const t = text.trim();
    if (!t) return toast.error("Type a message first");
    const title = t.slice(0, 40);
    try {
      const r = await api.post("/templates", { title, text: t });
      setTemplates((prev) => [...prev, r.data]);
      toast.success("Template saved");
    } catch (_) {
      toast.error("Failed to save template");
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  if (!contact) {
    return (
      <div className={`flex-1 ${mobileVisible ? "flex" : "hidden md:flex"}`}>
        <EmptyState />
      </div>
    );
  }

  const STATUS_LABEL = { new: "New", in_progress: "In Progress", resolved: "Resolved" };
  const STATUS_COLOR = {
    new: "bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A]",
    in_progress: "bg-[#DBEAFE] text-[#1E40AF] hover:bg-[#BFDBFE]",
    resolved: "bg-[#D1FAE5] text-[#065F46] hover:bg-[#A7F3D0]",
  };

  return (
    <section
      className={`flex-1 flex-col h-full bg-[var(--bg)] ${mobileVisible ? "flex" : "hidden md:flex"}`}
      data-testid="chat-window"
    >
      {/* Header */}
      <div className="h-16 px-3 md:px-5 border-b border-[var(--border)] bg-[var(--surface)] flex items-center gap-2 md:gap-3">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden h-8 w-8 shrink-0 -ml-1"
            onClick={onBack}
            title="Back"
            data-testid="chat-back-btn"
          >
            <CaretLeft size={18} weight="bold" />
          </Button>
        )}
        <Avatar className="h-9 w-9 md:h-10 md:w-10 shrink-0">
          <AvatarFallback className="bg-[var(--fg)] text-[var(--bg)] text-xs">
            {initials(contact.name, contact.phone)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[15px] heading truncate">{contact.name || contact.phone}</div>
          <div className="text-xs text-[var(--muted)] font-mono flex items-center gap-1">
            <Phone size={12} /> +{contact.phone}
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCallDialog(true)}
          className="h-8 gap-1.5 text-xs px-2 md:px-3 border-[#86EFAC] text-[#065F46] hover:bg-[#D1FAE5]"
          data-testid="call-driver-btn"
          title="Call driver with TTS voice message"
        >
          <PhoneCall size={14} weight="fill" />
          <span className="hidden md:inline">Call</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={toggleBot}
          className={`h-8 gap-1.5 text-xs px-2 md:px-3 ${contact.bot_paused ? "border-[#FDE68A] text-[#92400E] hover:bg-[#FEF3C7]" : "border-[#A7F3D0] text-[#065F46] hover:bg-[#D1FAE5]"}`}
          data-testid="bot-toggle-btn"
          title={contact.bot_paused ? "Bot paused - click to resume" : "Bot active - click to pause"}
        >
          <Robot size={14} weight="fill" />
          <span className="hidden md:inline">{contact.bot_paused ? "Bot off" : "Bot on"}</span>
        </Button>

        <div className="relative hidden md:block">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <Input
            value={inChatQuery}
            onChange={(e) => setInChatQuery(e.target.value)}
            placeholder="Search in chat…"
            className="pl-8 h-8 w-[180px] bg-[var(--bg)] border-[var(--border)] text-xs"
            data-testid="in-chat-search"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 px-3 rounded-full text-xs ${STATUS_COLOR[contact.status] || STATUS_COLOR.new}`}
              data-testid="status-pill"
            >
              {STATUS_LABEL[contact.status] || "New"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Set status</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <DropdownMenuItem key={k} onClick={() => updateStatus(k)}>
                {v}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {contact.status === "resolved" ? (
          <Button
            variant="outline"
            size="sm"
            onClick={reopenTicket}
            className="h-8 gap-1.5 text-xs px-2 md:px-3 border-[#FDE68A] text-[#92400E] hover:bg-[#FEF3C7]"
            data-testid="reopen-ticket-btn"
            title="Re-open this ticket"
          >
            <ArrowCounterClockwise size={14} weight="bold" />
            <span className="hidden md:inline">Re-open</span>
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={closeTicket}
            className="h-8 gap-1.5 text-xs px-2 md:px-3 border-[#A7F3D0] text-[#065F46] hover:bg-[#D1FAE5]"
            data-testid="close-ticket-btn"
            title="Mark this ticket as done"
          >
            <CheckCircle size={14} weight="fill" />
            <span className="hidden md:inline">Close</span>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs px-2 md:px-3" data-testid="assign-btn">
              <UserCircle size={14} />
              <span className="hidden sm:inline">
                {contact.assigned_to
                  ? (contact.assigned_to.full_name || contact.assigned_to.email || "").split("@")[0]
                  : "Assign"}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Transfer chat to</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => assign(null)}>
              <X size={14} className="mr-2" /> Unassigned
            </DropdownMenuItem>
            {users.map((u) => (
              <DropdownMenuItem key={u.id} onClick={() => assign(u.id)} data-testid={`transfer-to-${u.id}`}>
                {u.full_name || u.email}
                <span className="ml-auto text-[10px] text-[var(--muted)]">{u.role}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="ghost" size="icon" className="hidden md:inline-flex h-8 w-8" onClick={exportCsv} title="Export CSV" data-testid="export-csv">
          <DownloadSimple size={16} />
        </Button>
      </div>

      {/* Messages */}
      {contact.bot_last_run && (
        <div
          className={`px-4 py-1.5 text-[11px] border-b border-[var(--border)] flex items-center gap-1.5 ${
            contact.bot_last_run.result === "error" ? "bg-[#FEE2E2] text-[#991B1B]" : contact.bot_last_run.result === "replied" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[var(--surface)] text-[var(--muted)]"
          }`}
          data-testid="bot-last-run"
        >
          <Robot size={12} weight="fill" />
          Bot ({formatISTDateTime(contact.bot_last_run.at)}): {contact.bot_last_run.result} — {contact.bot_last_run.detail}
        </div>
      )}
      {/* Messages */}
      <div className="flex-1 overflow-y-auto scroll-thin px-3 md:px-6 py-4 md:py-5">
        <div className="mx-auto max-w-2xl flex flex-col gap-3">
          {inChatQuery && (
            <div className="text-[11px] text-[var(--muted)] text-center">
              Showing {filteredMessages.length} of {messages.length} matching "{inChatQuery}"
            </div>
          )}
          <AnimatePresence initial={false}>
            {filteredMessages.map((m) => {
              const outgoing = m.direction === "outgoing";
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className={`flex ${outgoing ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${m.id}`}
                >
                  <div className="group relative max-w-[80%]">
                    <div
                      className={`px-4 py-2 shadow-sm ${
                        outgoing
                          ? "bg-[var(--fg)] text-[var(--bg)] rounded-2xl rounded-tr-sm"
                          : "bg-[var(--surface)] border border-[var(--border)] text-[var(--fg)] rounded-2xl rounded-tl-sm"
                      }`}
                    >
                      {(m.media || m.location) && (
                        <MediaBubble message={m} outgoing={outgoing} />
                      )}
                      {!m.media && !m.location && (
                        <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                          {m.template_meta?.header && (
                            <div className={`font-bold mb-1 ${outgoing ? "text-[var(--bg)]" : ""}`}>
                              {m.template_meta.header}
                            </div>
                          )}
                          {m.text || <em className="opacity-70">[empty]</em>}
                          {m.template_meta?.footer && (
                            <div className={`text-[11px] mt-1.5 ${outgoing ? "text-[var(--bg)]/60" : "text-[var(--muted)]"}`}>
                              {m.template_meta.footer}
                            </div>
                          )}
                          {m.type === "template" && m.template_meta?.name && (
                            <div className={`mt-2 inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full ${
                              outgoing ? "bg-[#25D366]/25 text-[#b6ffce]" : "bg-[#25D366]/10 text-[#0f7a3c]"
                            }`}>
                              <span className="h-1 w-1 rounded-full bg-current" /> Template · {m.template_meta.name}
                            </div>
                          )}
                        </div>
                      )}
                      <div
                        className={`text-[10px] mt-1 flex items-center gap-1 ${
                          outgoing ? "justify-end text-[var(--bg)]/60" : "text-[var(--muted)]"
                        }`}
                      >
                        <span>{formatISTDateTime(m.timestamp)}</span>
                        {outgoing && <Tick status={m.status} />}
                      </div>
                      {m.sent_by && outgoing && (
                        <div
                          className="text-[10px] mt-0.5 flex items-center gap-1 justify-end font-medium text-[var(--bg)]/85"
                          data-testid={`replied-by-${m.id}`}
                        >
                          <UserCircle size={11} weight="fill" />
                          <span>Replied by {m.sent_by.full_name || (m.sent_by.email || "").split("@")[0]}</span>
                        </div>
                      )}
                    </div>

                    {m.reactions && Object.values(m.reactions).length > 0 && (
                      <div
                        className={`mt-1 flex flex-wrap gap-1 ${
                          outgoing ? "justify-end" : "justify-start"
                        }`}
                      >
                        {Object.values(m.reactions).map((e, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-1.5 py-0.5 rounded-full bg-[var(--surface)] border border-[var(--border)] shadow-sm"
                          >
                            {e}
                          </span>
                        ))}
                      </div>
                    )}

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={`absolute top-0 ${
                            outgoing ? "-left-9" : "-right-9"
                          } h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-[var(--surface)] border border-[var(--border)] shadow-sm`}
                          title="React"
                        >
                          <Smiley size={14} />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-auto p-1 flex gap-1">
                        {["❤️", "👍", "👎", "😂", "😮", "🙏"].map((e) => (
                          <button
                            key={e}
                            type="button"
                            onClick={() => react(m.id, e)}
                            className="text-xl hover:scale-125 transition-transform p-1"
                          >
                            {e}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => react(m.id, "")}
                          className="text-xs px-2 opacity-60"
                          title="Remove"
                        >
                          ✕
                        </button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
          <div ref={endRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="px-3 md:px-5 py-3 border-t border-[var(--border)] bg-[var(--surface)]">
        <form
          className="flex items-center gap-1.5 md:gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          data-testid="chat-composer"
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,video/*,audio/*,application/pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadAndSend(f);
              e.target.value = "";
            }}
            data-testid="chat-file-input"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 shrink-0"
            title="Attach image or PDF"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid="attach-btn"
          >
            {uploading ? <CircleNotch size={18} className="animate-spin" /> : <Paperclip size={18} />}
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-10 w-10 shrink-0"
                title="Quick replies"
                data-testid="quick-reply-btn"
              >
                <BookmarkSimple size={18} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-0">
              <div className="p-3 border-b border-[var(--border)]">
                <div className="text-xs font-semibold heading">Quick replies</div>
                <div className="text-[11px] text-[var(--muted)] mt-0.5">
                  Click to insert, or save current draft as template.
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto scroll-thin">
                {templates.length === 0 ? (
                  <div className="p-4 text-xs text-[var(--muted)]">No templates yet.</div>
                ) : (
                  <ul>
                    {templates.map((t) => (
                      <li key={t.id} className="border-b border-[var(--border)] last:border-0">
                        <div className="p-3 flex items-start gap-2 hover:bg-[var(--bg)]">
                          <button
                            type="button"
                            onClick={() => setText(t.text)}
                            className="flex-1 text-left"
                            data-testid={`template-${t.id}`}
                          >
                            <div className="text-xs font-medium">{t.title}</div>
                            <div className="text-[11px] text-[var(--muted)] line-clamp-2 mt-0.5">
                              {t.text}
                            </div>
                          </button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => deleteTemplate(t.id)}
                          >
                            <Trash size={12} />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="p-2 border-t border-[var(--border)]">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5 h-8 text-xs"
                  onClick={addTemplate}
                  data-testid="save-template-btn"
                >
                  <Plus size={12} /> Save current text as template
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a reply…"
            className="h-10 bg-[var(--bg)] border-[var(--border)]"
            disabled={sending}
            data-testid="chat-message-input"
          />
          <Button
            type="submit"
            className="h-10 gap-1.5 md:min-w-[100px] px-3 md:px-4 bg-[var(--fg)] hover:opacity-90 text-[var(--bg)]"
            disabled={sending || !text.trim()}
            data-testid="send-message-button"
          >
            {sending ? (
              <>
                <CircleNotch size={14} className="animate-spin" /> <span className="hidden md:inline">Sending</span>
              </>
            ) : (
              <>
                <PaperPlaneTilt size={14} weight="fill" /> <span className="hidden md:inline">Send</span>
              </>
            )}
          </Button>
        </form>
        <div className="text-[11px] text-[var(--muted)] mt-1.5 hidden sm:block">
          WhatsApp 24h rule: free-form reply tabhi bhej sakte jab driver ne last 24h me message bheja ho.
        </div>
      </div>
      <CallDriverDialog
        open={showCallDialog}
        onOpenChange={setShowCallDialog}
        contact={contact}
      />
    </section>
  );
}
